import { z } from '@customerio/hydra-validation';
import type { DeliveryDetail } from '@/services/db/delivery/entity';
import { capitalize, isEmptyString } from '@/utils/string';

/**
 * Derives the Inbox Config panel view-model from a delivery detail — a port of
 * the Ember `cio-delivery-detail/inbox-config` getters. The inbox payload
 * (`detail.body`) is parsed leniently: an invalid or empty body degrades to the
 * default-priority-only config, matching the Ember component's `{}` fallback.
 */

/** Inbox priority integers → labels (Ember `INBOX_PRIORITY_LABELS`). */
const PRIORITY_LABELS: Record<number, string> = { 1: 'low', 5: 'medium', 10: 'high' };
/** Ember `INBOX_DEFAULT_PRIORITY` (Medium) — used when the payload omits a priority. */
const DEFAULT_PRIORITY = 5;

const InboxPayloadSchema = z.object({
  priority: z.coerce.number().optional().catch(undefined),
  expiry: z.string().optional().catch(undefined),
  type: z.string().optional().catch(undefined),
  topics: z.array(z.string()).optional().catch(undefined),
});

function parseInboxPayload(body: string): z.infer<typeof InboxPayloadSchema> {
  let data: unknown = {};
  try {
    data = JSON.parse(body);
  } catch {
    return {};
  }
  const parsed = InboxPayloadSchema.safeParse(data);
  return parsed.success ? parsed.data : {};
}

/** The priority label (Ember `inboxMessagePriorityLabel`); defaults to Medium, falls back to the raw number. */
export function inboxPriorityLabel(priority: number | undefined): string {
  if (priority == null) return PRIORITY_LABELS[DEFAULT_PRIORITY];
  return PRIORITY_LABELS[priority] ?? String(priority);
}

/** The full Inbox Config view-model consumed by `<InboxConfig>`. */
export interface InboxConfigView {
  priority: string;
  expiration: string | undefined;
  type: string | undefined;
  topics: string[];
}

export function buildInboxConfig(detail: DeliveryDetail): InboxConfigView {
  const payload = parseInboxPayload(detail.body);

  return {
    priority: capitalize(inboxPriorityLabel(payload.priority)),
    expiration: isEmptyString(payload.expiry) ? undefined : payload.expiry,
    type: isEmptyString(payload.type) ? undefined : payload.type,
    topics: payload.topics ?? [],
  };
}
